// Build the tenant theme from one color. Keep text and controls readable for pale brands too.
import { palette } from '@primevue/themes';
import tinycolor from 'tinycolor2';

// Tint Tailwind's neutral scale with the brand hue.
const NEUTRAL_GRAY = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
};
const NEUTRAL_SAT = 0.1; // 0 is pure gray
const neutral = (step, hue) =>
  tinycolor({ h: hue, s: NEUTRAL_SAT, l: tinycolor(NEUTRAL_GRAY[step]).toHsl().l }).toHexString();

const AA_TEXT = 4.5;
const MIN_INDICATOR = 3; // WCAG minimum for controls

// Text that can be read on one primary fill: white, or a darkened version of the fill itself.
// Every fill gets its own ink, so the brand color never has to change to make room for the text.
function inkOn(fill) {
  if (tinycolor.readability('#ffffff', fill) >= AA_TEXT) return '#ffffff';

  const ink = tinycolor(fill).darken(35);

  for (let i = 0; i < 20 && tinycolor.readability(ink, fill) < AA_TEXT; i += 1) {
    ink.darken(4);
  }

  return ink.toHexString();
}

// Darken or lighten a color until it has enough contrast with the background.
function accessibleOn(seed, bg, target = AA_TEXT) {
  const darken = tinycolor(bg).getLuminance() > 0.5;
  const c = tinycolor(seed);

  for (let i = 0; i < 15 && tinycolor.readability(c, bg) < target; i += 1) {
    if (darken) c.darken(4);
    else c.lighten(4);
  }

  return c.toHexString();
}

// Make pale selected states visible. Keep gray seeds gray.
function brandTint(seed, l) {
  const { h, s } = tinycolor(seed).toHsl();
  return tinycolor({ h, s: s < 0.05 ? s : Math.min(Math.max(s, 0.3), 0.5), l }).toHexString();
}

function isValidHex(hex) {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

// Bulma needs separate hue, saturation and lightness values.
function applyBulmaPrimary(primary, contrast) {
  const { h, s, l } = tinycolor(primary).toHsl();
  const root = document.documentElement.style;

  root.setProperty('--bulma-primary-h', h);
  root.setProperty('--bulma-primary-s', `${s * 100}%`);
  root.setProperty('--bulma-primary-l', `${l * 100}%`);

  // Bulma buttons and tags build their text from the primary hue and this lightness alone, so the
  // composed --bulma-primary-invert never reaches them. Our ink is a darkened primary, so its hue
  // and saturation already match and only the lightness has to travel.
  root.setProperty('--bulma-primary-invert-l', `${tinycolor(contrast).toHsl().l * 100}%`);
}

// Set the design system tokens and return the colors Buefy needs.
export function applyPrimary(seed, dark = false) {
  if (!isValidHex(seed)) return undefined;

  const root = document.documentElement.style;
  const pal = palette(seed);

  for (const [step, hex] of Object.entries(pal)) {
    root.setProperty(`--color-primary-${step}`, hex);
  }

  // Tint shadows with the darkest brand shade.
  const { r, g, b } = tinycolor(pal[950]).toRgb();
  root.setProperty('--shadow-color', `${r} ${g} ${b}`);

  const hue = tinycolor(seed).toHsl().h;

  for (const step of Object.keys(NEUTRAL_GRAY)) {
    root.setProperty(`--color-neutral-${step}`, neutral(step, hue));
  }

  // One ink per fill: 500 for normal, 600 for hover, 700 for active.
  const contrast = inkOn(pal[500]);

  root.setProperty('--color-primary-contrast', contrast);
  root.setProperty('--color-primary-contrast-600', inkOn(pal[600]));
  root.setProperty('--color-primary-contrast-700', inkOn(pal[700]));

  // Make links readable on the card.
  const bg_card = dark ? neutral(900, hue) : '#ffffff';

  root.setProperty('--color-link', accessibleOn(seed, bg_card));
  root.setProperty('--color-text-selection', brandTint(seed, dark ? 0.3 : 0.8));

  // Controls need a lower contrast ratio than text.
  root.setProperty('--color-focus-ring', accessibleOn(pal[400], bg_card, MIN_INDICATOR));
  root.setProperty('--color-accent', accessibleOn(pal[500], bg_card, MIN_INDICATOR));

  // Pale brands need stronger selected colors. Dark mode uses the preset values.
  if (dark) {
    root.removeProperty('--p-app-selected');
    root.removeProperty('--p-app-selected-hover');
  } else {
    root.setProperty('--p-app-selected', brandTint(seed, 0.92));
    root.setProperty('--p-app-selected-hover', brandTint(seed, 0.88));
  }

  return { primary: pal[500], contrast };
}

// Apply the same primary color and text color to Buefy and PrimeVue.
export default function applyTheme(theme_primary_color) {
  const color = tinycolor(theme_primary_color);
  if (!color.isValid()) return;

  const { primary, contrast } = applyPrimary(color.toHexString());
  applyBulmaPrimary(primary, contrast);
}
